import { useState, type ReactNode } from 'react';
import { cn } from 'cn';
import {
  Columns2,
  FoldHorizontal,
  RectangleHorizontal,
  Languages,
  Rows2,
  Sparkles,
  UnfoldHorizontal,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipProvider } from '@/components/ui/tooltip';
import {
  LEADINGS,
  LEADING_LABELS,
  READER_STEPS,
  READING_WIDTHS,
  READING_WIDTH_LABELS,
  type Leading,
  type ReaderStep,
  type ReadingWidth,
} from './readerSettings';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { VIEW_LABELS, VIEW_MODES, type ViewMode } from './viewMode';

/**
 * The reader toolbar: **one row** — view · word meaning · language · Aa · Ask.
 *
 * It used to be four control groups stacked above the text, consuming the
 * first ~220px of the screen before a single word of the khutbah. Reading
 * settings are set once and rarely touched; the khutbah is why anyone is
 * here. Size and leading now live behind `Aa`, which is where a reader looks
 * for them anyway.
 *
 * Two rules from the design system that are easy to get wrong:
 *
 * - **Text size changes `--reader-step` on the reader root**, and every script
 *   scales from it. Never Arabic alone — the point of the control is that the
 *   Arabic and the translation keep their relationship.
 * - **Line height changes `--reader-leading`**, and `globals.css` already adds
 *   0.45 for Urdu and 0.2 for Arabic on top. That is a legibility requirement,
 *   not a taste setting: Nastaliq and tashkeel collide without it.
 */

export interface ReaderControlsProps {
  languages: readonly string[];
  languageNames: Readonly<Record<string, string>>;
  language: string;
  onLanguageChange: (language: string) => void;
  step: ReaderStep;
  onStepChange: (step: ReaderStep) => void;
  leading: Leading;
  onLeadingChange: (leading: Leading) => void;
  view: ViewMode;
  onViewChange: (view: ViewMode) => void;
  /** Whether the text fills the pane or narrows to the reading measure. */
  readingWidth: ReadingWidth;
  onReadingWidthChange: (width: ReadingWidth) => void;
  /** Whether Arabic words are tappable for their meaning. */
  wordMeaning: boolean;
  onWordMeaningChange: (on: boolean) => void;
  /** Null until there is any learning content for this khutbah. */
  glossedWords: number | null;
  /** Opens the outline sheet. Only reachable where the rail is not shown. */
  onOpenOutline: () => void;
  /**
   * Whether the outline is showing as a permanent rail. When it is not — below
   * `lg`, or at full reading width where the rail stands down to give the
   * khutbah the display — this button is the only way to the outline, so it
   * has to appear at every width rather than below `lg` alone.
   */
  outlineInRail: boolean;
  /** Whether the chat panel is showing. */
  chatOpen: boolean;
  /** Shows or hides the chat panel, at any width. */
  onToggleChat: () => void;
}

/**
 * Narrow, normal, full — read left to right as the width they produce, so the
 * control's shape says which is which before the tooltip does.
 */
const WIDTH_ICONS: Record<ReadingWidth, ReactNode> = {
  measure: <FoldHorizontal className="size-4" aria-hidden="true" />,
  normal: <RectangleHorizontal className="size-4" aria-hidden="true" />,
  full: <UnfoldHorizontal className="size-4" aria-hidden="true" />,
};

function Segmented<T extends string>({
  label,
  options,
  value,
  onChange,
  labels,
  icons,
  hideOnPhone,
}: {
  label: string;
  options: readonly T[];
  value: T;
  onChange: (next: T) => void;
  labels: Readonly<Record<T, string>>;
  /**
   * An icon per option. Where given, the control is icon-only and the label
   * becomes the accessible name and the tooltip — never dropped, only moved.
   */
  icons?: Readonly<Record<T, ReactNode>>;
  hideOnPhone?: (option: T) => boolean;
}) {
  return (
    <div
      role="group"
      aria-label={label}
      className="flex shrink-0 rounded-md border border-input p-0.5"
    >
      {options.map((option) => {
        const icon = icons?.[option];
        const button = (
          <button
            key={option}
            type="button"
            aria-pressed={option === value}
            // Icon-only, so the label has to be the accessible name. Without
            // this the control reads as four unnamed buttons.
            {...(icon ? { 'aria-label': labels[option] } : {})}
            onClick={() => onChange(option)}
            className={cn(
              'inline-flex items-center justify-center rounded-sm',
              // A fixed square per option, so the four segments are the same
              // width and the group stops being ragged. The word labels were
              // 80-130px each and set the segment widths from the text, which
              // is what made them look unaligned.
              icon ? 'size-9' : 'h-9 px-2.5 text-[13px]',
              option === value
                ? 'bg-secondary text-secondary-foreground'
                : 'text-muted-foreground hover:text-foreground',
              hideOnPhone?.(option) ? 'hidden md:inline-flex' : undefined,
            )}
          >
            {icon ?? labels[option]}
          </button>
        );
        return icon ? (
          <Tooltip key={option} label={labels[option]}>
            {button}
          </Tooltip>
        ) : (
          button
        );
      })}
    </div>
  );
}

/**
 * The four view modes as icons.
 *
 * Three of them are the pane layout itself — two columns, two rows, one pane —
 * which is what the mode actually changes, so the icon is the thing rather
 * than a symbol for it. The fourth cannot be: translation-only and
 * Arabic-only are both a single pane, so a pane shape could not tell them
 * apart. They take the script instead — the `Languages` glyph for the reading
 * language, and a real Arabic letter for the original, set in the Arabic face
 * so it is the script it denotes.
 */
const VIEW_ICONS: Readonly<Record<ViewMode, ReactNode>> = {
  'side-by-side': <Columns2 className="size-4" aria-hidden="true" />,
  stacked: <Rows2 className="size-4" aria-hidden="true" />,
  translation: <Languages className="size-4" aria-hidden="true" />,
  arabic: (
    <span lang="ar" className="script-ar text-base leading-none" aria-hidden="true">
      ع
    </span>
  ),
};

/**
 * The `.script-*` class for a language code.
 *
 * <p>Local to the toolbar because the option list is the only place that needs
 * a script for a language it is not currently showing; everywhere else the
 * server sends `scriptClass` for the one language in play.
 */
function scriptClassOf(code: string): string {
  switch (code) {
    case 'ar':
      return 'script-ar';
    case 'ml':
      return 'script-ml';
    case 'kn':
      return 'script-kn';
    case 'ta':
      return 'script-ta';
    case 'ur':
      return 'script-ur';
    default:
      return 'script-latin';
  }
}

export function ReaderControls({
  languages,
  languageNames,
  language,
  onLanguageChange,
  step,
  onStepChange,
  leading,
  onLeadingChange,
  view,
  onViewChange,
  readingWidth,
  onReadingWidthChange,
  wordMeaning,
  onWordMeaningChange,
  glossedWords,
  onOpenOutline,
  outlineInRail,
  chatOpen,
  onToggleChat,
}: ReaderControlsProps) {
  const [typeOpen, setTypeOpen] = useState(false);
  const stepIndex = READER_STEPS.indexOf(step);
  // Indexed access can be undefined under noUncheckedIndexedAccess, and the
  // honest fallback is the current step — which makes the button a no-op
  // rather than asserting the index is in range with a `!`.
  const smaller = READER_STEPS[Math.max(0, stepIndex - 1)] ?? step;
  const larger =
    READER_STEPS[Math.min(READER_STEPS.length - 1, stepIndex + 1)] ?? step;

  return (
    // One provider for the toolbar, so the 300ms delay and the "move between
    // icon buttons without re-waiting" behaviour are shared by all of them.
    <TooltipProvider>
    <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
      {/* Only below lg, where the rail is not on screen. */}
      <Button
        type="button"
        variant="outline"
        size="lg"
        onClick={onOpenOutline}
        className={cn(outlineInRail && 'lg:hidden')}
      >
        Sections
      </Button>

      <Segmented
        label="View"
        options={VIEW_MODES}
        value={view}
        onChange={onViewChange}
        labels={VIEW_LABELS}
        icons={VIEW_ICONS}
        // Side by side needs two columns. It stays offered on a phone rather
        // than hidden, because effectiveMode() falls back to stacked there —
        // so the setting a reader chose on a laptop survives.
        hideOnPhone={(option) => option === 'side-by-side'}
      />

      {/*
        Width, as three segments rather than one toggle.

        It was a single button that swapped between two widths, which worked
        while there were two. With a middle setting a toggle would have to
        cycle, and a cycling control cannot be aimed: a reader wanting the
        narrow column from full width would have to pass through normal and
        guess how many presses they were from where they wanted to be. Three
        segments show all three states and let one be chosen directly, and it
        is the same control the view switch beside it already uses.

        Hidden below md, where the pane is already narrower than the measure
        and every setting would be the same no-op.
      */}
      <div className="hidden md:block">
        <Segmented
          label="Reading width"
          options={READING_WIDTHS}
          value={readingWidth}
          onChange={onReadingWidthChange}
          labels={READING_WIDTH_LABELS}
          icons={WIDTH_ICONS}
        />
      </div>

      {/*
        Word meaning works in every view, including Translation-only, where
        it brings the Arabic in as chips beneath each passage — the kit's
        answer, and better than disabling the control there.

        Unavailable only when the khutbah has no glossed words at all, which
        is most of them today. The reason is said rather than left to be
        guessed from a dead toggle.
      */}
      <Button
        type="button"
        variant={wordMeaning ? 'secondary' : 'outline'}
        size="lg"
        aria-pressed={wordMeaning}
        disabled={glossedWords === 0}
        title={
          glossedWords === 0
            ? 'No words in this khutbah have been glossed yet'
            : undefined
        }
        onClick={() => onWordMeaningChange(!wordMeaning)}
      >
        Word meaning
        {glossedWords !== null && glossedWords > 0 ? (
          <span className="ms-1 text-xs text-muted-foreground">{glossedWords}</span>
        ) : null}
      </Button>

      {/*
        The language picker.

        An application-rendered list, not a native <select>. The options are
        five scripts, and a native select lets the operating system choose each
        one's font and size -- which came out at mixed sizes and misaligned, and
        no CSS could reach it. Each option now carries its own script class, as
        the design system's one-font-per-script rule requires.
      */}
      <label className="flex items-center gap-1.5 text-sm">
        <span className="whitespace-nowrap text-muted-foreground">Language</span>
        <Select value={language} onValueChange={onLanguageChange}>
          <SelectTrigger aria-label="Reading language" className="min-w-28">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {languages.map((code) => (
              <SelectItem
                key={code}
                value={code}
                // Its own language and script, so Nastaliq is Nastaliq and
                // Malayalam is Malayalam, at one size.
                lang={code}
                className={scriptClassOf(code)}
              >
                {languageNames[code] ?? code}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </label>

      {/* Size and leading, behind one button. Set once, rarely changed. */}
      {/* `ms-auto` starts the trailing group: reading settings, then the
          one action that opens a panel. */}
      <div className="relative ms-auto">
        <Button
          type="button"
          variant="outline"
          size="lg"
          aria-expanded={typeOpen}
          onClick={() => setTypeOpen((was) => !was)}
        >
          <span aria-hidden>Aa</span>
          <span className="sr-only">Text size and line height</span>
        </Button>

        {typeOpen ? (
          <div
            role="dialog"
            aria-label="Text size and line height"
            onMouseLeave={() => setTypeOpen(false)}
            className="absolute top-full end-0 z-40 mt-1.5 flex w-64 flex-col gap-3 rounded-lg border border-border bg-popover p-3 shadow-2"
          >
            <div className="flex items-center justify-between gap-2">
              <span className="text-[13px] font-medium">Text size</span>
              <span className="flex items-center gap-1">
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  className="size-9"
                  disabled={stepIndex <= 0}
                  aria-label="Smaller text"
                  onClick={() => onStepChange(smaller)}
                >
                  <span aria-hidden>A−</span>
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  className="size-9"
                  disabled={stepIndex >= READER_STEPS.length - 1}
                  aria-label="Larger text"
                  onClick={() => onStepChange(larger)}
                >
                  <span aria-hidden>A+</span>
                </Button>
              </span>
            </div>

            <div className="flex flex-col gap-1.5">
              <span className="text-[13px] font-medium">Line height</span>
              <Segmented
                label="Line height"
                options={LEADINGS}
                value={leading}
                onChange={onLeadingChange}
                labels={LEADING_LABELS}
              />
            </div>
          </div>
        ) : null}
      </div>
      {/*
        Ask AI, at every width.

        It used to be `xl:hidden`, on the reasoning that above xl the panel is
        docked so a button to open it would do nothing. But there was then no
        way to *close* the docked panel either -- a reader reported exactly
        that. It is a toggle now, and the panel is dismissible at any width.
      */}
      <Button
        type="button"
        variant={chatOpen ? 'secondary' : 'default'}
        size="lg"
        aria-pressed={chatOpen}
        onClick={onToggleChat}
        // Pushed to the trailing edge, away from the reading settings. It is
        // the one action here that opens something rather than adjusting how
        // the page looks, and it was wedged between the language picker and
        // `Aa` as though it were another of them.
      >
        <Sparkles className="size-4" aria-hidden="true" />
        Ask AI
      </Button>

    </div>
    </TooltipProvider>
  );
}
